<template>
    <div class="l-meeting-goal-section">
        <!-- Headline -->
        <div
            data-test-id="meeting-goal-toggle"
            class="clickable mouseover-parent"
            tabindex="0"
            @click="toggleGoals()"
            @keyup.enter="toggleGoals()"
        >
            <span v-if="showGoals">
                <span class="agenda-question">{{ $t("meetings.labelGoalsHide") }}</span>
                <i class="fa fa-caret-down color-grey l-default-margin-s mouseover"></i>
            </span>
            <span v-else>
                <span class="agenda-question">{{ $t("meetings.labelGoals") }}</span>
                <i class="fa fa-caret-right color-grey l-default-margin-s mouseover"></i>
            </span>
        </div>
        <!--/Headline -->

        <!-- List -->
        <div v-if="showGoals">
            <!-- Other Participants Goals -->
            <div v-for="participant in otherParticipants" :key="participant._id">
                <div class="tw-mt-4 tw-bg-gray-50 tw-p-3 tw-mb-0 tw-rounded-lg">
                    <l-profile-picture :user="participant" size="s" />

                    <span class="l-default-margin">
                        <span>
                            {{ participant.firstname }}
                            {{ participant.lastname }}
                        </span>
                    </span>
                </div>

                <div
                    v-for="goal in participant.goals"
                    :key="goal._id"
                    data-test-class="meeting-goals-item"
                >
                    <l-goal-list-item-short :goal="goal"></l-goal-list-item-short>
                </div>
                <!-- KR edit modal -->
                <l-goal-k-r-edit-modal
                    v-show="$route.query.editGoalId && $route.query.editKeyResultId"
                    :goal="editGoal"
                    @update="goalUpdated"
                ></l-goal-k-r-edit-modal>
                <!--/KR edit modal -->

                <l-no-data
                    v-show="!participant.goals.length"
                    :icon="icon"
                    :text="
                        $t(`meetings.listEmptyGoalsOpposite`, {
                            firstname: participant.firstname,
                        })
                    "
                    data-test-id="participant-meeting-goal-list-no-data"
                />
            </div>
            <!--/Other Participants Goals -->

            <!-- Current User Goals -->
            <div class="tw-mt-4 tw-bg-gray-50 tw-p-3 tw-mb-0 tw-rounded-lg">
                <l-profile-picture :user="currentUser" size="s" />

                <span class="l-default-margin">
                    <span>
                        {{ currentUser.firstname }}
                        {{ currentUser.lastname }}
                    </span>
                </span>
            </div>

            <div
                v-for="goal in currentUserGoalList"
                :key="goal._id"
                data-test-class="meeting-goals-item"
            >
                <l-goal-list-item-short :goal="goal"></l-goal-list-item-short>
            </div>

            <l-no-data
                v-show="!currentUserGoalList.length"
                :icon="icon"
                :text="
                    $t(`meetings.listEmptyGoalsOpposite`, {
                        firstname: currentUser.firstname,
                    })
                "
                data-test-id="current-user-meeting-goal-list-no-data"
            />
            <!--/Current User Goals -->
        </div>
        <!--/List -->
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import LGoalListItemShort from "@/components/LGoalListItemShort";
import LNoData from "@/components/LNoData";
import LProfilePicture from "@/components/LProfilePicture";
import LGoalKREditModal from "@/components/LGoalKREditModal";
import icon from "@/img/icons/goals.png";

export default {
    components: {
        LGoalListItemShort,
        LNoData,
        LProfilePicture,
        LGoalKREditModal,
    },
    props: {
        meeting: {
            type: Object,
            required: true,
        },
        meetingModeEnabled: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            showGoals: false,
        };
    },
    computed: {
        ...mapGetters({
            goalList: "goals/goals/list",
            currentUser: "users/current/user",
        }),
        editGoal() {
            const goalId = this.$route.query.editGoalId;
            const goals = this.otherParticipants.map(participant => participant.goals).flat();
            goals.push(...this.currentUserGoalList);

            return goals?.find(g => g._id === goalId) || {};
        },
        otherParticipants() {
            return this.meeting.otherUsers.map(participant => ({
                ...participant,
                goals: this.goalList.filter(
                    goal => goal.status !== "archived" && goal.directOwner === participant._id,
                ),
            }));
        },
        currentUserGoalList() {
            if (this.meetingModeEnabled) {
                return this.goalList.filter(
                    goal =>
                        goal.status !== "archived" &&
                        goal.directOwner === this.currentUser._id &&
                        goal.visibility === "all",
                );
            }

            return this.goalList.filter(
                goal => goal.status !== "archived" && goal.directOwner === this.currentUser._id,
            );
        },
    },
    created() {
        this.icon = icon;
        if (this.$route.query.editGoalId) {
            // in the mode of editing a goal kr, if user refresh, we need to expand the KR.
            this.toggleGoals();
        }
    },
    methods: {
        goalUpdated() {
            // Goal progress calculation only happens on the server, so we need to reload the whole list
            // once the modal is closed.
            //
            this.fetchGoals();
        },
        toggleGoals() {
            this.showGoals = !this.showGoals;

            if (this.showGoals) {
                this.fetchGoals();
            }
        },
        fetchGoals() {
            const users = [...this.meeting.otherUsers, this.currentUser].map(participant =>
                participant._id ? participant._id : participant,
            );

            this.$store.dispatch({
                type: "goals/goals/getList",
                payload: {
                    listSegment: "custom",
                    listCustomFilter: {
                        users,
                    },
                    output: "list",
                },
            });
        },
    },
};
</script>

<style scoped></style>
